import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { FiX } from 'react-icons/fi';

interface NewUserForm {
  userName: string;
  email: string;
  role: string;
  organization: string;
}

interface AddNewUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddUser: (user: NewUserForm) => void;
}

const AddNewUserModal: React.FC<AddNewUserModalProps> = ({
  isOpen,
  onClose,
  onAddUser,
}) => {
  const [formData, setFormData] = useState<NewUserForm>({
    userName: '',
    email: '',
    role: '',
    organization: ''
  });

  // Available organizations for selection
  const availableOrganizations = [
    { id: '1', name: 'Saudi Aramco' },
    { id: '2', name: 'NEOM' },
    { id: '3', name: 'King Abdullah University' },
    { id: '4', name: 'Saudi Digital Government' },
    { id: '5', name: 'Ministry of Energy' },
    { id: '6', name: 'Saudi Vision 2030' },
    { id: '7', name: 'Smart Cities Authority' },
    { id: '8', name: 'Digital Transformation Unit' }
  ];

  const roles = [
    { value: 'owner', label: 'Owner' },
    { value: 'admin', label: 'Admin' },
    { value: 'developer', label: 'Developer' },
    { value: 'designer', label: 'Designer' },
    { value: 'viewer', label: 'Viewer' }
  ];

  const updateFormField = (field: keyof NewUserForm, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    // Validate that all fields are filled
    if (formData.userName && formData.email && formData.role && formData.organization) {
      onAddUser(formData);
      handleClose();
    }
  };

  const handleClose = () => {
    // Reset form
    setFormData({
      userName: '',
      email: '',
      role: '',
      organization: ''
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60 z-50" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[95vw] max-w-md max-h-[90vh]">
          <div className="bg-[#1A1A1A] rounded-lg border border-white/[0.08] shadow-lg flex flex-col max-h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/[0.08] flex-shrink-0">
              <h2 className="text-lg font-semibold text-white">
                Add New User
              </h2>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <FiX size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <input
                    type="text"
                    value={formData.userName}
                    onChange={(e) => updateFormField('userName', e.target.value)}
                    placeholder="Enter User Name"
                    className="w-full bg-[#2A2A2A] border border-white/[0.08] rounded-md px-3 py-2 text-white text-sm"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateFormField('email', e.target.value)}
                    placeholder="Enter Email"
                    className="w-full bg-[#2A2A2A] border border-white/[0.08] rounded-md px-3 py-2 text-white text-sm"
                  />
                </div>
                <div>
                  <select
                    value={formData.organization}
                    onChange={(e) => updateFormField('organization', e.target.value)}
                    className="w-full bg-[#2A2A2A] border border-white/[0.08] rounded-md px-3 py-2 text-white text-sm"
                  >
                    <option value="">Select Organization</option>
                    {availableOrganizations.map(org => (
                      <option key={org.id} value={org.name}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <select
                    value={formData.role}
                    onChange={(e) => updateFormField('role', e.target.value)}
                    className="w-full bg-[#2A2A2A] border border-white/[0.08] rounded-md px-3 py-2 text-white text-sm"
                  >
                    <option value="">Select Role</option>
                    {roles.map(role => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end p-6 border-t border-white/[0.08] flex-shrink-0">
              <button
                onClick={handleSubmit}
                className="px-6 py-2 bg-transparent border border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-white rounded-md transition-colors font-medium"
              >
                ADD USER
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default AddNewUserModal; 